import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import {
  Button,
  Icon,
  Label,
  Popup,
  Segment,
  type SemanticICONS,
} from "semantic-ui-react";

import type { OpenAPI } from "simplydo/interfaces";

import { annualImpactValue, generateImpactText } from "./statementUtils";

type ImpactStatement =
  OpenAPI.GET<"/ideas/{idea_id}/impactStatement">["response"]["impactStatements"]["userEstimate"];

type StageSegmentProps = {
  stage: string;
  statementDefinition: OpenAPI.Schemas["Challenge"]["impactStatements"];
  impactStatement?: ImpactStatement;
  onView?: () => void;
  onCreate?: () => void;
  onDelete?: () => void;
  isCreating?: boolean;
  canEditStage?: boolean;
};

export const useImpactStageMap = () => {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      userEstimate: {
        name: "User Estimate",
        icon: "wpforms" as SemanticICONS,
        description: `This estimate is based on the estimation of the user who created the ${t("generic.idea")}.`,
      },
      projectEstimate: {
        name: "Project Estimate",
        icon: "object group outline" as SemanticICONS,
        description: ` This estimate is based on the estimation as part of the ${t("generic.idea")}'s assessment. It is only visible to project assignees and ${t("generic.challenge")} managers.`,
        create: "Create estimate",
      },
      finalOutcome: {
        name: "Final Outcome",
        icon: "line graph" as SemanticICONS,
        description: `This statement is used to track the realised ${t("generic.impact")} of this ${t("generic.idea")}. It is only visible to project assignees and ${t("generic.challenge")} managers.`,
        create: "Create outcome",
      },
    }),
    [t],
  );
};

export const StageSegment = ({
  stage,
  statementDefinition,
  impactStatement,
  onView,
  onCreate,
  onDelete,
  isCreating,
  canEditStage,
}: StageSegmentProps) => {
  const { t } = useTranslation();

  const segmentMap = useImpactStageMap();

  const def = useMemo(() => segmentMap[stage], [segmentMap, stage]);

  const visibleImpacts = useMemo(() => {
    if (!impactStatement?.impacts) {
      return [];
    }

    const impacts: (typeof impactStatement.impacts)[number][] = [];
    const impactOptions = statementDefinition?.impacts || {};
    for (const optionId in impactOptions) {
      const impactOption = impactOptions[optionId];
      if (impactOption.enabled) {
        const impact = impactStatement.impacts[optionId];

        if (
          impact?.enabled &&
          impact?.ownerImpact &&
          // @ts-ignore
          (impact?.choice || impact?.value)
        ) {
          impacts.push(impact);
        }
      }
    }

    return impacts;
  }, [impactStatement, statementDefinition]);

  const getLabelStyle = (effect?: string) => {
    if (effect === "cost") {
      return { borderColor: "#f2b8b5", color: "#9f3a38" };
    }
    if (effect === "saving") {
      return { borderColor: "#c2e0c6", color: "#2c662d" };
    }
    return {};
  };

  return (
    <Segment secondary={!impactStatement}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          <h3>
            <Icon name={def.icon} /> {def.name}
          </h3>
          {def.description}
        </div>
        {impactStatement ?
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: "8px",
              }}
            >
              {visibleImpacts.map((impact, index) => (
                <Label
                  basic
                  style={getLabelStyle(impact.ownerImpact?.effect)}
                  key={index}
                >
                  <Popup
                    position="right center"
                    content={generateImpactText(true, impact, t)}
                    trigger={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                        }}
                      >
                        <div>
                          <Icon name={impact.ownerImpact?.icon} />
                          {impact.ownerImpact?.effect === "cost" ? "-" : ""}
                          {annualImpactValue(true, impact)}
                        </div>
                        {impact.choice?.weight !== undefined ?
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <Icon
                              name="level up alternate"
                              style={{ marginRight: 2 }}
                            />
                            {impact.choice?.weight || 0}/
                            {impact.ownerImpact?.maxWeight || 0}
                          </div>
                        : null}
                      </div>
                    }
                  />
                </Label>
              ))}
            </div>
            <div style={{ display: "flex", flex: 1, marginTop: "1em" }}>
              <Button style={{ flex: 1 }} onClick={onView}>
                <Icon name="eye" />
                View details
              </Button>
              {stage !== "userEstimate" && canEditStage && (
                <>
                  <Button style={{ flex: 1 }} onClick={onView}>
                    <Icon name="pencil" />
                    Edit Details
                  </Button>
                  {onDelete ?
                    <Button
                      icon
                      basic
                      onClick={() => {
                        onDelete();
                      }}
                    >
                      <Icon name="trash" />
                    </Button>
                  : null}
                </>
              )}
            </div>
          </>
        : def.create ?
          <Button
            secondary
            onClick={() => {
              onCreate?.();
            }}
            loading={isCreating}
          >
            <Icon name="plus" />
            {def.create}
          </Button>
        : null}
      </div>
    </Segment>
  );
};
